import {Button, Stack, Typography, useTheme} from "@mui/joy";
import {useSessionCookie} from "../../hooks/useSessionCookie";
import React from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Compass} from "lucide-react";
import {appRoute} from "../../utils/routes";
import Main from "../../pages/Main";
import GuestNavbar from "../Layout/GuestNavbar";

/**
 * Catch-all page for unmatched routes, wired as `path: "*"` in every router.
 *
 * It sits outside the `Main` layout so a signed-out visitor who mistypes a URL
 * gets this page rather than a guard redirect, which is why the home link is
 * resolved here instead of being hardcoded: the signed-in landing page differs
 * per host (`/app` on the test router, `/` on the app subdomain), and a
 * signed-out visitor belongs on the guest home either way.
 */
const NotFound = () => {
    const theme = useTheme()
    const {t} = useTranslation()
    const {userSession} = useSessionCookie()

    const signedIn = !!userSession
    const homePath = signedIn ? appRoute() : "/"

    const content = <Stack flex={1} minHeight={{xs: "60dvh", md: "70dvh"}} justifyContent={"center"} alignItems={"center"} gap={1}
                           padding={3}>
        <Compass style={{color: theme.palette.secondary['500'], fontSize: 90}}/>

        <Typography level={"h2"} textAlign={"center"}>
            {t('notFoundTitle')}
        </Typography>

        <Typography level={"body-lg"} textAlign={"center"}>
            {t('notFoundDescription')}
        </Typography>

        <Button component={Link} to={homePath} variant={"outlined"} color={"secondary"} size={"lg"}
                sx={{marginTop: 1, width: "14rem"}}>
            {t('notFoundActionLabel')}
        </Button>
    </Stack>

    // Keep the visitor's way around: the app's own navigation when signed in, the guest navbar otherwise.
    return signedIn ?
        <Main>{content}</Main> :
        <Stack minHeight={"100dvh"}>
            <GuestNavbar/>
            {content}
        </Stack>
}

export default NotFound
